import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Star, Phone, Globe, Clock, ArrowLeft,
  Sparkles, ExternalLink, Menu, X, Loader2, CalendarCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="glass sticky top-0 z-50 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight">lokalnie.pro</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/szukaj" className="text-sm font-medium text-muted-foreground hover:text-foreground">
              Katalog firm
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground">
                  Panel
                </Link>
                <Button variant="ghost" onClick={logout}>Wyloguj</Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost">Zaloguj</Button></Link>
                <Link to="/rejestracja">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white">Rozpocznij</Button>
                </Link>
              </>
            )}
          </nav>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </header>
  );
};

const StarRating = ({ rating, size = "default" }) => {
  const starSize = size === "large" ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${starSize} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      ))}
    </div>
  );
};

export default function BusinessPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasLanding, setHasLanding] = useState(false);

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await axios.get(`${API}/businesses/${businessId}`);
        setBusiness(response.data);
        setHasLanding(response.data.has_landing_page);
      } catch (error) {
        console.error('Error fetching business:', error);
        toast.error('Nie znaleziono firmy');
        navigate('/szukaj');
      } finally {
        setLoading(false);
      }
    };
    fetchBusiness();
  }, [businessId, navigate]);

  const handleGenerateLanding = async () => {
    if (!user) {
      toast.error('Zaloguj się, aby wygenerować landing page');
      navigate('/login');
      return;
    }

    setGenerating(true);
    try {
      await axios.post(`${API}/landing-pages/generate/${businessId}`, {}, { withCredentials: true });
      setHasLanding(true);
      toast.success('Landing page został wygenerowany!');
      navigate(`/landing/${businessId}`);
    } catch (error) {
      console.error('Error generating landing:', error);
      // Serwer mowi KONKRETNIE czego brakuje (np. klucza LLM) — pokazujemy to
      // uzytkownikowi zamiast ogolnika. Audyt 2026-08-23, F4.
      toast.error(error?.response?.data?.detail || 'Błąd podczas generowania strony');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-64 bg-slate-200 rounded-xl" />
            <div className="h-8 bg-slate-200 rounded w-1/2" />
            <div className="h-4 bg-slate-200 rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!business) return null;

  const needsWebsite = !business.website || business.website.includes('facebook.com');

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Powrót
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative aspect-video rounded-xl overflow-hidden"
            >
              <img
                src={business.photos?.[0] || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800'}
                alt={business.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <Badge className="bg-white/90 text-slate-700 backdrop-blur-sm">
                  {business.category}
                </Badge>
              </div>
              {needsWebsite && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-amber-500 text-white">
                    Potrzebuje strony WWW
                  </Badge>
                </div>
              )}
            </motion.div>

            {/* Business Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" data-testid="business-name">
                    {business.name}
                  </h1>

                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <StarRating rating={Math.round(business.rating)} size="large" />
                      <span className="font-semibold text-lg">{business.rating}</span>
                      <span className="text-slate-500">({business.reviews_count} opinii)</span>
                    </div>
                    {business.slotify_slug && (
                      <a href="#zarezerwuj">
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <CalendarCheck className="w-4 h-4 mr-2" />
                          Zarezerwuj online
                        </Button>
                      </a>
                    )}
                  </div>

                  {business.description && (
                    <>
                      <Separator className="my-4" />
                      <div>
                        <h2 className="font-semibold text-lg mb-2">O firmie</h2>
                        <p className="text-slate-600 leading-relaxed">{business.description}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Slotify booking widget — prominent CTA, zaraz po Business Info */}
            {business.slotify_slug && (
              <motion.div
                id="zarezerwuj"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Card className="overflow-hidden border-emerald-300 shadow-lg">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5 text-white">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                        <CalendarCheck className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="font-bold text-xl">Zarezerwuj online</h2>
                        <p className="text-sm text-emerald-50">
                          Wybierz uslugodawce, date i godzine. Potwierdzenie dostaniesz na e-mail w kilka sekund.
                        </p>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-0">
                    <iframe
                      title={`Rezerwacja online — ${business.name}`}
                      src={`https://slotify.dev.sorto.ai/widget/${business.slotify_slug}?lang=pl&theme=light`}
                      style={{ width: "100%", minHeight: "850px", border: "none", display: "block" }}
                      loading="lazy"
                    />
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Generate Landing CTA */}
            {needsWebsite && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-slate-900 mb-1">
                          Ta firma nie ma strony internetowej
                        </h3>
                        <p className="text-slate-600 text-sm">
                          AI wygeneruje profesjonalny landing page na podstawie danych z Google Maps
                        </p>
                      </div>
                      {hasLanding ? (
                        <Link to={`/landing/${businessId}`} data-testid="view-landing-btn">
                          <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Zobacz stronę
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          onClick={handleGenerateLanding}
                          disabled={generating}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          data-testid="generate-landing-btn"
                        >
                          {generating ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generowanie...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Wygeneruj stronę
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardContent className="p-0">
                  <div className="h-64 rounded-xl overflow-hidden">
                    <MapContainer
                      center={[business.latitude, business.longitude]}
                      zoom={15}
                      className="h-full w-full"
                      scrollWheelZoom={false}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[business.latitude, business.longitude]}>
                        <Popup>{business.name}</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-semibold text-lg">Kontakt</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Adres</p>
                        <p className="text-sm text-slate-600">{business.address}</p>
                        <p className="text-sm text-slate-600">{business.city}</p>
                      </div>
                    </div>

                    {business.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">Telefon</p>
                          <a href={`tel:${business.phone}`} className="text-sm text-amber-600 hover:underline">
                            {business.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {business.website && (
                      <div className="flex items-start gap-3">
                        <Globe className="w-5 h-5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">Strona</p>
                          <a 
                            href={business.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-amber-600 hover:underline break-all"
                          >
                            {business.website}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Opening Hours */}
            {business.opening_hours && Object.keys(business.opening_hours).length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <h3 className="font-semibold text-lg">Godziny otwarcia</h3>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(business.opening_hours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between text-sm">
                          <span className="text-slate-500">{day}</span>
                          <span className="font-medium">{hours}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
